import React, { useState, useCallback, Suspense, lazy, useRef, useEffect } from 'react'
import type { LiteClient } from 'algoliasearch/lite'
import { logEvent } from '../../Utils/analytics/firAnalytics'
import type { SearchTab } from './types'
import { SEARCH_TABS } from './types'
import { SearchWrapper } from './styles'
import { SearchFallback } from './components'
import { ANALYTICS_TAGS } from '../../Constants/analytics'

const InstantSearchWrapper = lazy(() =>
  import(
    /* webpackChunkName: "instantsearch-wrapper" */ './components/InstantSearchWrapper'
  ).then((module) => ({ default: module.InstantSearchWrapper }))
)

const FOCUS_TIMEOUT = 200
const CLICK_OUTSIDE_TIMEOUT = 100

export const AutocompleteContainer: React.FC = () => {
  const [searchClient, setSearchClient] = useState<LiteClient | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [activeTab, setActiveTab] = useState<SearchTab>(SEARCH_TABS.ALL)

  const containerRef = useRef<HTMLDivElement>(null)
  const isFocusingIntoContainerRef = useRef(false)
  const lastMouseDownTargetRef = useRef<Node | null>(null)

  useEffect(() => {
    const loadSearchClient = async (): Promise<void> => {
      try {
        const { searchClient: client } = await import(
          /* webpackChunkName: "algolia-client" */ '../../Api/search/Algolia'
        )
        setSearchClient(client)
        setIsLoading(false)
      } catch (error) {
        console.warn('Failed to load search client:', error)
        setIsLoading(false)
      }
    }

    loadSearchClient()
  }, [])

  const resetSearchState = useCallback(() => {
    setIsFocused(false)
    setQuery('')
    setIsModalOpen(false)
    setActiveTab(SEARCH_TABS.ALL)
  }, [])

  const setFocusingFlag = useCallback(() => {
    isFocusingIntoContainerRef.current = true
    setTimeout(() => {
      isFocusingIntoContainerRef.current = false
    }, FOCUS_TIMEOUT)
  }, [])

  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery)
    setIsModalOpen(newQuery.length > 0)
  }, [])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      lastMouseDownTargetRef.current = e.target as Node
      setFocusingFlag()
    },
    [setFocusingFlag]
  )

  const handleFocus = useCallback(() => {
    setFocusingFlag()
    setIsFocused(true)
    if (query.length > 0) {
      setIsModalOpen(true)
    }
  }, [query.length, setFocusingFlag])

  const handleFocusIn = useCallback(() => {
    setFocusingFlag()
  }, [setFocusingFlag])

  useEffect(() => {
    if (!isFocused || !containerRef.current) return undefined

    const container = containerRef.current

    const handleClickOutside = (e: MouseEvent): void => {
      if (isFocusingIntoContainerRef.current) return

      const target = e.target as Node
      lastMouseDownTargetRef.current = target

      if (!container.contains(target)) {
        setTimeout(() => {
          if (isFocusingIntoContainerRef.current) return

          const activeElement = document.activeElement
          if (activeElement && !container.contains(activeElement)) {
            resetSearchState()
          }
        }, CLICK_OUTSIDE_TIMEOUT)
      }
    }

    const handleFocusOut = (e: FocusEvent): void => {
      if (isFocusingIntoContainerRef.current) return

      const relatedTarget = e.relatedTarget as Node | null
      if (relatedTarget && container.contains(relatedTarget)) return

      const lastMouseDownTarget = lastMouseDownTargetRef.current
      if (lastMouseDownTarget && container.contains(lastMouseDownTarget)) return

      const activeElement = document.activeElement
      if (activeElement && container.contains(activeElement)) return

      setTimeout(() => {
        if (isFocusingIntoContainerRef.current) return

        const currentActiveElement = document.activeElement
        if (currentActiveElement && !container.contains(currentActiveElement)) {
          resetSearchState()
        }
      }, CLICK_OUTSIDE_TIMEOUT)
    }

    document.addEventListener('mousedown', handleClickOutside)
    container.addEventListener('focusin', handleFocusIn)
    container.addEventListener('focusout', handleFocusOut)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      container.removeEventListener('focusin', handleFocusIn)
      container.removeEventListener('focusout', handleFocusOut)
    }
  }, [isFocused, handleFocusIn, resetSearchState])

  const handleClose = useCallback(() => {
    resetSearchState()
  }, [resetSearchState])

  const handleTabChange = useCallback(
    (tab: SearchTab) => {
      setFocusingFlag()
      setActiveTab(tab)
      logEvent(ANALYTICS_TAGS.search_tab_change, { tab })
    },
    [setFocusingFlag]
  )

  if (isLoading || !searchClient) {
    return <SearchFallback />
  }

  return (
    <SearchWrapper ref={containerRef} $isExpanded={isFocused}>
      <Suspense fallback={<SearchFallback />}>
        <InstantSearchWrapper
          searchClient={searchClient}
          query={query}
          isModalOpen={isModalOpen}
          activeTab={activeTab}
          onQueryChange={handleQueryChange}
          onFocus={handleFocus}
          onBlur={() => {}}
          onMouseDown={handleMouseDown}
          onClose={handleClose}
          onTabChange={handleTabChange}
        />
      </Suspense>
    </SearchWrapper>
  )
}
